import React, { useEffect, useRef, useState } from 'react';
import { IDSButton } from '@inera/ids-react';
import ConfirmDialog from '../ConfirmDialog';
import StatusAlert from '../StatusAlert';
import { fetchAllTkbRules, uploadTkbDocument, deleteTkbRules } from '../../services/tkbRulesAPI';
import { formatDateTimeNoSeconds } from '../../utils/dateTime';

const StatusMsg = ({ error, success }) => (
  <StatusAlert error={error} success={success} className="ids-mt-2" />
);

const formatDate = (iso) => formatDateTimeNoSeconds(iso);

const TkbAdminLayout = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({ error: null, success: null });
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchAllTkbRules()
      .then(setRules)
      .catch((err) => setLoadError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploading(true);
    setUploadStatus({ error: null, success: null });
    try {
      const created = await uploadTkbDocument(file);
      setRules((prev) => {
        const ids = new Set(created.map((r) => r.id));
        const kept = prev.filter((r) => !ids.has(r.id));
        return [...kept, ...created];
      });
      setUploadStatus({ error: null, success: `${created.length} kontrakt uppdaterade från "${file.name}".` });
    } catch (err) {
      setUploadStatus({ error: err.message, success: null });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    const id = confirmDeleteId;
    setConfirmDeleteId(null);
    try {
      await deleteTkbRules(id);
      setRules((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      setUploadStatus({ error: err.message, success: null });
    }
  };

  const confirmTarget = rules.find((r) => r.id === confirmDeleteId);

  return (
    <main className="ids-content ids-p-6" style={{ maxWidth: '860px', margin: '2rem auto' }}>
      {confirmTarget && (
        <ConfirmDialog
          message={`Ta bort TKB-regler för "${confirmTarget.contractName} ${confirmTarget.version}"?`}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}

      <h1 className="ids-heading-l ids-mb-2">TKB-dokument</h1>
      <p style={{ color: '#4b5563', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
        Ladda upp ett TKB-dokument (.docx) för att extrahera fältregler per tjänstekontrakt.
        Befintliga regler för ett kontrakt ersätts om kontraktet redan finns.
      </p>

      {/* Upload */}
      <section style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '1.25rem', marginBottom: '2rem' }}>
        <h2 className="ids-heading-s ids-mb-3">Ladda upp TKB-dokument</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <IDSButton size="s" type="button" disabled={uploading} onClick={() => fileInputRef.current?.click()}>
            {uploading ? 'Bearbetar...' : 'Välj .docx-fil'}
          </IDSButton>
          <input
            ref={fileInputRef}
            type="file"
            accept=".docx"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          {uploading && (
            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              Extraherar tabeller och analyserar med AI – detta kan ta en stund…
            </span>
          )}
        </div>
        <StatusMsg error={uploadStatus.error} success={uploadStatus.success} />
      </section>

      {/* Rules table */}
      <section>
        <h2 className="ids-heading-s ids-mb-3">Uppladdade TKB-regler</h2>
        {loading && <p style={{ color: '#6b7280' }}>Laddar...</p>}
        {loadError && <StatusAlert error={loadError} />}
        {!loading && !loadError && rules.length === 0 && (
          <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Inga TKB-regler uppladdade ännu.</p>
        )}
        {rules.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                <th style={{ padding: '0.5rem 0.75rem' }}>Kontrakt</th>
                <th style={{ padding: '0.5rem 0.75rem' }}>Version</th>
                <th style={{ padding: '0.5rem 0.75rem' }}>Domän</th>
                <th style={{ padding: '0.5rem 0.75rem' }}>Uppladdad</th>
                <th style={{ padding: '0.5rem 0.75rem' }}>Av</th>
                <th style={{ padding: '0.5rem 0.75rem' }}></th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '0.5rem 0.75rem', fontWeight: 500 }}>{r.contractName}</td>
                  <td style={{ padding: '0.5rem 0.75rem', color: '#6b7280' }}>{r.version}</td>
                  <td style={{ padding: '0.5rem 0.75rem', color: '#6b7280' }}>{r.domain || '–'}</td>
                  <td style={{ padding: '0.5rem 0.75rem', color: '#6b7280' }}>{formatDate(r.uploadedAt)}</td>
                  <td style={{ padding: '0.5rem 0.75rem', color: '#6b7280' }}>{r.uploadedBy}</td>
                  <td style={{ padding: '0.5rem 0.75rem' }}>
                    <IDSButton size="s" secondary type="button" onClick={() => setConfirmDeleteId(r.id)}>
                      Ta bort
                    </IDSButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </main>
  );
};

export default TkbAdminLayout;
